const ATTRIBUTION_KEY = "bestsalescoach_attribution";
const BLOCKED_KEYS = new Set([
  "first_name",
  "last_name",
  "name",
  "full_name",
  "email",
  "phone",
  "message",
  "notes",
  "free_text",
  "company_message",
]);

function canUseSessionStorage() {
  try {
    return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
  } catch (error) {
    return false;
  }
}

function looksLikeEmail(value) {
  return typeof value === "string" && /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.test(value);
}

function safeRandomId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `sess_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

function currentPagePath() {
  if (typeof window === "undefined") {
    return "/home";
  }

  return `${window.location.pathname}${window.location.hash || ""}`;
}

function landingPagePath() {
  if (typeof window === "undefined") {
    return "/home";
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash || ""}`;
}

function readStoredAttribution() {
  if (!canUseSessionStorage()) {
    return {};
  }

  try {
    const raw = window.sessionStorage.getItem(ATTRIBUTION_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    return {};
  }
}

function writeStoredAttribution(payload) {
  if (!canUseSessionStorage()) {
    return;
  }

  try {
    window.sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(payload));
  } catch (error) {
    // Fail safely when sessionStorage is blocked or unavailable.
  }
}

export function getOrCreateSessionId() {
  const stored = readStoredAttribution();

  if (stored.session_id) {
    return stored.session_id;
  }

  const sessionId = safeRandomId();
  writeStoredAttribution({ ...stored, session_id: sessionId });
  return sessionId;
}

export function getAttributionParams() {
  const stored = readStoredAttribution();
  const currentUrl =
    typeof window !== "undefined" ? new URL(window.location.href) : new URL("https://www.bestsalescoach.ai");

  const payload = {
    session_id: stored.session_id || getOrCreateSessionId(),
    landing_page: stored.landing_page || landingPagePath(),
    current_page: currentPagePath(),
    referrer:
      stored.referrer ||
      (typeof document !== "undefined" && document.referrer ? document.referrer : ""),
    utm_source: stored.utm_source || currentUrl.searchParams.get("utm_source") || "",
    utm_medium: stored.utm_medium || currentUrl.searchParams.get("utm_medium") || "",
    utm_campaign: stored.utm_campaign || currentUrl.searchParams.get("utm_campaign") || "",
    utm_content: stored.utm_content || currentUrl.searchParams.get("utm_content") || "",
    utm_term: stored.utm_term || currentUrl.searchParams.get("utm_term") || "",
    first_seen_at: stored.first_seen_at || new Date().toISOString(),
  };

  writeStoredAttribution(payload);
  return payload;
}

export function sanitizeProperties(properties = {}) {
  const cleaned = {};

  Object.entries(properties).forEach(([key, value]) => {
    if (BLOCKED_KEYS.has(key)) {
      return;
    }

    if (value === undefined || value === null || value === "") {
      return;
    }

    if (typeof value === "string" && looksLikeEmail(value)) {
      return;
    }

    cleaned[key] = value;
  });

  return cleaned;
}

export function trackEvent(eventName, properties = {}) {
  const sanitizedProperties = sanitizeProperties(properties);
  const debugEnabled =
    typeof process !== "undefined" &&
    (process.env.VITE_ANALYTICS_DEBUG === "true" ||
      process.env.REACT_APP_ANALYTICS_DEBUG === "true");

  if (debugEnabled && typeof console !== "undefined") {
    console.debug("[analytics]", eventName, sanitizedProperties);
  }

  if (typeof window !== "undefined" && typeof window.plausible === "function") {
    try {
      window.plausible(eventName, { props: sanitizedProperties });
    } catch (error) {
      if (debugEnabled && typeof console !== "undefined") {
        console.debug("[analytics:error]", eventName, error);
      }
    }
  }
}

export function trackPageView(properties = {}) {
  trackEvent("page_view", properties);
}
